import * as React from "react";
import { useState, useContext, createContext, useEffect } from "react";
import { makeStyles, Theme, createStyles } from "@mui/styles";
import {ValueContext} from '~common/components/form-def-field-fc';
import { TextField }  from "~common/components/fields/text-field-fc";
import {ICGFormControl} from '~common/components/icg-form-control';
import {Grid} from '@mui/material';
import {cloneDeep} from 'lodash'
import AutocompleteField from './autocomplete-field-fc';
import translate from "../i18n/i18n"

export const Address1Context = createContext();
export const Address2Context = createContext();
export const Address3Context = createContext();
export const CityContext = createContext();
export const CountyContext = createContext();
export const StateContext = createContext();
export const PostalCodeContext = createContext();
export const CountryContext = createContext();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      width: "100%",
    },
    displayText: {
      width: "100%",
      // marginTop: 16,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  })
);

interface AddressFieldProps {
  fieldDefinition: any;
  policyMessages?: any;
  readOnlyOverride?: boolean;
  onAccessRootData?: any;
  path?: string;
  onValidate?: any;
}

export const AddressField: React.FC<AddressFieldProps> = ({
  fieldDefinition,
  policyMessages,
  readOnlyOverride,
  onAccessRootData,
  path,
  onValidate
}) => {
  const classes = useStyles();    

  // Setting the inital value and setValue hook based on the context passed in from form def field, instead of useState
  // setFieldVal will pass the value up to the parent component that houses the context.

  const [fieldVal, setFieldVal] = useContext(ValueContext);


  var address1PropsVal = null 
  var address2PropsVal = null 
  var address3PropsVal = null 
  var cityPropsVal = null 
  var countyPropsVal = null 
  var statePropsVal = null 
  var postalCodePropsVal = null 
  var countryPropsVal = null 
  
  if (fieldVal) {
    if (fieldVal.address1) {
      address1PropsVal = fieldVal.address1;
    }
    if (fieldVal.address2) {
      address2PropsVal = fieldVal.address2;
    } 
    if (fieldVal.address3) {
      address3PropsVal = fieldVal.address3;
    } 
    if (fieldVal.city) {
      cityPropsVal = fieldVal.city;
    } 
    if (fieldVal.county) {
      countyPropsVal = fieldVal.county;
    } 
    if (fieldVal.state) {
      statePropsVal = fieldVal.state;
    } 
    if (fieldVal.postalCode) {
      postalCodePropsVal = fieldVal.postalCode;
    } 
    if (fieldVal.country) {
      countryPropsVal = fieldVal.country;
    }   
  }




  const [address1LocalVal, setAddress1LocalVal] = useState(address1PropsVal);
  const [address2LocalVal, setAddress2LocalVal] = useState(address2PropsVal);
  const [address3LocalVal, setAddress3LocalVal] = useState(address3PropsVal);
  const [cityLocalVal, setCityLocalVal] = useState(cityPropsVal);
  const [countyLocalVal, setCountyLocalVal] = useState(countyPropsVal);
  const [stateLocalVal, setStateLocalVal] = useState(statePropsVal);
  const [postalCodeLocalVal, setPostalCodeLocalVal] = useState(postalCodePropsVal);
  const [countryLocalVal, setCountryLocalVal] = useState(countryPropsVal);

  

  const filterPolicyMessages = (policyMessages, subField) => {
    if (policyMessages) {

      const applicable = policyMessages.reduce((acc, m): Array<any> => {
        var headerMessage = false
        if (m && m.extendedAttributes ) {
          if (m.extendedAttributes.fields && m.extendedAttributes.fields.length > 0) {
            for (let field of m.extendedAttributes.fields) {
              if (field[subField]) {
                // Formulated a sub field message based on the extended attributes and original message
                acc.push({
                  createdAt: m.createdAt,
                  message: field[subField],
                  title: field[subField],
                  path: m.path,
                  severity: m.severity,
                  parentEntityID: m.parentEntityID,
                  parentEntityType: m.parentEntityType,
                  policyEntityID: m.policyEntityID,
                  policyEntityType: m.policyEntityType
                });
              }
            }
            
          } else {
            headerMessage = true
          }
        } else {
          headerMessage = true
        }

        if (subField == "header" && headerMessage) {
          acc.push({
            createdAt: m.createdAt,
            message: m.message,
            title: m.title,
            path: m.path,
            severity: m.severity,
            parentEntityID: m.parentEntityID,
            parentEntityType: m.parentEntityType,
            policyEntityID: m.policyEntityID,
            policyEntityType: m.policyEntityType
          });
        }

        return acc;
      }, []);
      
      return applicable
    } else {
      return null
    }
  };

  // This function formulates fake field defintions based for each sub field
  // These are based on the original Address Field definition, our expected default behavior, and any overrides inside addressAttributes
  // BD would suggest not looking at this function too much unless you want to alter a default or add a new prop to addressAttributes. You're solutions are likely elsewhere.
  const formulateSubDefinitions = (subField:string) => {
    switch (subField) {
      case "address1":
        var address1Attributes = {}
        var address1GridAttrbutes = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
          xl: 12
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.address1) {
          address1Attributes = fieldDefinition.addressAttributes.address1
          if (fieldDefinition.addressAttributes.address1.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.address1.gridAttributes
            if (fdga.xs) {
              address1GridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              address1GridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              address1GridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              address1GridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              address1GridAttrbutes.xl = fdga.xl
            }

          }
        }

        var address1DefaultMode = fieldDefinition.mode

        var address1FieldDef = {
          gridAttributes: address1GridAttrbutes,
          title: address1Attributes.label || translate("Address1") == "Address1" ? "Address 1" : translate("Address1"),
          // helpText: address1Attributes.label,
          mode: address1Attributes.mode || address1DefaultMode,
          key: fieldDefinition.key+"Address1",
          minLength:  address1Attributes.minLength,
          maxLength:   address1Attributes.maxLength,
          mask: address1Attributes.mask,
          placeholder: address1Attributes.placeholder,
          allowedCharacters: address1Attributes.allowedCharacters,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
          validationAttributes: fieldDefinition.validationAttributes
        }
        return address1FieldDef

      case "address2":
        var address2Attributes = {}
        var address2GridAttrbutes = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
          xl: 12
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.address2) {
          address2Attributes = fieldDefinition.addressAttributes.address2
          if (fieldDefinition.addressAttributes.address2.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.address2.gridAttributes
            if (fdga.xs) {
              address2GridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              address2GridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              address2GridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              address2GridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              address2GridAttrbutes.xl = fdga.xl
            }

          }
        }

        var address2DefaultMode = fieldDefinition.mode
        if (fieldDefinition.mode == "Required") {
          address2DefaultMode = "Optional"
        }

        var address2FieldDef = {
          gridAttributes: address2GridAttrbutes,
          title: address2Attributes.label || translate("Address2") == "Address2" ? "Address 2" : translate("Address2"),
          helpText: address2Attributes.label,
          mode: address2Attributes.mode || address2DefaultMode,
          key: fieldDefinition.key+"Address2",
          minLength:  address2Attributes.minLength,
          maxLength:   address2Attributes.maxLength,
          mask: address2Attributes.mask,
          placeholder: address2Attributes.placeholder,
          allowedCharacters: address2Attributes.allowedCharacters,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
        }
        return address2FieldDef

      case "address3":
        var address3Attributes = {}
        var address3GridAttrbutes = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
          xl: 12
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.address3) {
          address3Attributes = fieldDefinition.addressAttributes.address3
          if (fieldDefinition.addressAttributes.address3.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.address3.gridAttributes
            if (fdga.xs) {
              address3GridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              address3GridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              address3GridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              address3GridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              address3GridAttrbutes.xl = fdga.xl
            }

          }
        }

        var address3DefaultMode = "Hidden"

        var address3FieldDef = {
          gridAttributes: address3GridAttrbutes,
          title: address3Attributes.label || translate("Address3") == "Address3" ? "Address 3" : translate("Address3"),
          helpText: address3Attributes.label,
          mode: address3Attributes.mode || address3DefaultMode,
          key: fieldDefinition.key+"Address3",
          minLength:  address3Attributes.minLength,
          maxLength:   address3Attributes.maxLength,
          mask: address3Attributes.mask,
          placeholder: address3Attributes.placeholder,
          allowedCharacters: address3Attributes.allowedCharacters,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
        }
        return address3FieldDef

      case "county":
        var countyAttributes = {}
        var countyGridAttrbutes = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
          xl: 12
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.county) {
          countyAttributes = fieldDefinition.addressAttributes.county
          if (fieldDefinition.addressAttributes.county.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.county.gridAttributes
            if (fdga.xs) {
              countyGridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              countyGridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              countyGridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              countyGridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              countyGridAttrbutes.xl = fdga.xl
            }

          }
        }
  
        var countyDefaultMode = "Hidden"

        var countyFieldDef = {
          gridAttributes: countyGridAttrbutes,
          title: countyAttributes.label || "County",
          helpText: countyAttributes.label,
          mode: countyAttributes.mode || countyDefaultMode,
          key: fieldDefinition.key+"County",
          minLength:  countyAttributes.minLength,
          maxLength:   countyAttributes.maxLength,
          mask: countyAttributes.mask,
          placeholder: countyAttributes.placeholder,
          allowedCharacters: countyAttributes.allowedCharacters,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
        }
        return countyFieldDef

      case "city":
        var cityAttributes = {}
        var cityGridAttrbutes = {
          xs: 6,
          sm: 6,
          md: 6,
          lg: 6,
          xl: 6
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.city) {
          cityAttributes = fieldDefinition.addressAttributes.city
          if (fieldDefinition.addressAttributes.city.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.city.gridAttributes
            if (fdga.xs) {
              cityGridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              cityGridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              cityGridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              cityGridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              cityGridAttrbutes.xl = fdga.xl
            }

          }
        }

        var cityDefaultMode = fieldDefinition.mode

        var cityFieldDef = {
          gridAttributes: cityGridAttrbutes,
          title: cityAttributes.label || translate("City"),
          helpText: cityAttributes.label,
          mode: cityAttributes.mode || cityDefaultMode,
          key: fieldDefinition.key+"City",
          minLength:  cityAttributes.minLength,
          maxLength:   cityAttributes.maxLength,
          mask: cityAttributes.mask,
          placeholder: cityAttributes.placeholder,
          allowedCharacters: cityAttributes.allowedCharacters,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
        }
        return cityFieldDef

      case "state":
        var stateAttributes = {}
        var stateGridAttrbutes = {
          xs: 2,
          sm: 2,
          md: 2,
          lg: 2,
          xl: 2
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.state) {
          stateAttributes = fieldDefinition.addressAttributes.state
          if (fieldDefinition.addressAttributes.state.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.state.gridAttributes
            if (fdga.xs) {
              stateGridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              stateGridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              stateGridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              stateGridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              stateGridAttrbutes.xl = fdga.xl
            }

          }
        }

        var stateDefaultMode = fieldDefinition.mode

        var stateFieldDef = {
          gridAttributes: stateGridAttrbutes,
          title: stateAttributes.label || translate("State"),
          helpText: stateAttributes.label,
          mode: stateAttributes.mode || stateDefaultMode,
          key: fieldDefinition.key+"State",
          minLength:  stateAttributes.minLength,
          maxLength:   stateAttributes.maxLength,
          mask: stateAttributes.mask,
          placeholder: stateAttributes.placeholder,
          allowedCharacters: stateAttributes.allowedCharacters,
          autoSuggestURL: stateAttributes.autoSuggestURL,
          probeAutoSuggestURL: stateAttributes.probeAutoSuggestURL,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
        }

        return stateFieldDef

      case "postalCode":
        var postalCodeAttributes = {}
        var postalCodeGridAttrbutes = {
          xs: 4,
          sm: 4,
          md: 4,
          lg: 4,
          xl: 4
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.postalCode) {
          postalCodeAttributes = fieldDefinition.addressAttributes.postalCode
          if (fieldDefinition.addressAttributes.postalCode.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.postalCode.gridAttributes
            if (fdga.xs) {
              postalCodeGridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              postalCodeGridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              postalCodeGridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              postalCodeGridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              postalCodeGridAttrbutes.xl = fdga.xl
            }

          }
        }

        var postalCodeDefaultMode = fieldDefinition.mode

        var postalCodeFieldDef = {
          gridAttributes: postalCodeGridAttrbutes,
          title: postalCodeAttributes.label || translate("Zip"),
          helpText: postalCodeAttributes.label,
          mode: postalCodeAttributes.mode || postalCodeDefaultMode,
          key: fieldDefinition.key+"PostalCode",
          minLength:  postalCodeAttributes.minLength,
          maxLength:   postalCodeAttributes.maxLength,
          mask: postalCodeAttributes.mask,
          placeholder: postalCodeAttributes.placeholder,
          allowedCharacters: postalCodeAttributes.allowedCharacters,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
        }
        return postalCodeFieldDef

      case "country":
        var countryAttributes = {}
        var countryGridAttrbutes = {
          xs: 12,
          sm: 12,
          md: 12,
          lg: 12,
          xl: 12
        }
        if (fieldDefinition && fieldDefinition.addressAttributes && fieldDefinition.addressAttributes.country) {
          countryAttributes = fieldDefinition.addressAttributes.country
          if (fieldDefinition.addressAttributes.country.gridAttributes) { 
            var fdga = fieldDefinition.addressAttributes.country.gridAttributes
            if (fdga.xs) {
              countryGridAttrbutes.xs = fdga.xs
            }
            if (fdga.sm) {
              countryGridAttrbutes.sm = fdga.sm
            }
            if (fdga.md) {
              countryGridAttrbutes.md = fdga.md
            }
            if (fdga.lg) {
              countryGridAttrbutes.lg = fdga.lg
            }
            if (fdga.xl) {
              countryGridAttrbutes.xl = fdga.xl
            }

          }
        }

        var countryDefaultMode = fieldDefinition.mode

        var countryFieldDef = {
          gridAttributes: countryGridAttrbutes,
          title: countryAttributes.label || translate("Country"),
          helpText: countryAttributes.label,
          mode: countryAttributes.mode || countryDefaultMode,
          key: fieldDefinition.key+"Country",
          minLength:  countryAttributes.minLength,
          maxLength:   countryAttributes.maxLength,
          mask: countryAttributes.mask,
          placeholder: countryAttributes.placeholder,
          allowedCharacters: countryAttributes.allowedCharacters,
          // Field Info for the entire address field is shown on the address 1 subfield only
          fieldInfo: fieldDefinition.fieldInfo,
        }
        return countryFieldDef
      default:
        console.warn("Address Sub Field not supported")
        return null
    }


  };

  var headerPolicyMessages = filterPolicyMessages(policyMessages, "header");
  var address1PolicyMessages = filterPolicyMessages(policyMessages, "address1");
  var address2PolicyMessages = filterPolicyMessages(policyMessages, "address2");
  var address3PolicyMessages = filterPolicyMessages(policyMessages, "address3");
  var cityPolicyMessages = filterPolicyMessages(policyMessages, "city");
  var countyPolicyMessages = filterPolicyMessages(policyMessages, "county");
  var statePolicyMessages = filterPolicyMessages(policyMessages, "state");
  var postalCodePolicyMessages = filterPolicyMessages(policyMessages, "postalCode");
  var countryPolicyMessages = filterPolicyMessages(policyMessages, "country");


  useEffect(() => {
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!address1LocalVal && newVal.address1 == "")) {
      return
    }

    if (address1LocalVal != newVal.address1) {
      newVal.address1 = address1LocalVal
      setFieldVal(newVal);
    }

  }, [address1LocalVal])

  useEffect(() => {
    
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!address2LocalVal && newVal.address2 == "")) {
      return
    }

    if (address2LocalVal != newVal.address2) {
      newVal.address2 = address2LocalVal
      setFieldVal(newVal);
    }

  }, [address2LocalVal])

  useEffect(() => {
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!address3LocalVal && newVal.address3 == "")) {
      return
    }

    if (address3LocalVal != newVal.address3) {
      newVal.address3 = address3LocalVal
      setFieldVal(newVal);
    }

  }, [address3LocalVal])

  useEffect(() => {
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!cityLocalVal && newVal.city == "")) {
      return
    }

    if (cityLocalVal != newVal.city) {
      newVal.city = cityLocalVal
      setFieldVal(newVal);
    }

  }, [cityLocalVal])

  useEffect(() => {
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!countyLocalVal && newVal.county == "")) {
      return
    }

    if (countyLocalVal != newVal.county) {
      newVal.county = countyLocalVal
      setFieldVal(newVal);
    }

  }, [countyLocalVal])

  useEffect(() => {
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!stateLocalVal && newVal.state == "")) {
      return
    }

    if (stateLocalVal != newVal.state) {
      newVal.state = stateLocalVal
      setFieldVal(newVal);
    }

  }, [stateLocalVal])

  useEffect(() => {
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!postalCodeLocalVal && newVal.postalCode == "")) {
      return
    }

    if (postalCodeLocalVal != newVal.postalCode) {
      newVal.postalCode = postalCodeLocalVal
      setFieldVal(newVal);
    }

  }, [postalCodeLocalVal])

  useEffect(() => {
    var newVal = cloneDeep(fieldVal)
    if (!newVal){
      newVal = {}
    }

    if ((!countryLocalVal && newVal.country == "")) {
      return
    }

    if (countryLocalVal != newVal.country) {
      newVal.country = countryLocalVal
      setFieldVal(newVal);
    }
    
  }, [countryLocalVal])

  useEffect(() => {

    if (fieldVal) {


      if (address1LocalVal !== fieldVal.address1) {
        setAddress1LocalVal(fieldVal.address1)
      }

      if (address2LocalVal !== fieldVal.address2) {
        setAddress2LocalVal(fieldVal.address2)
      }

      if (address3LocalVal !== fieldVal.address3) {
        setAddress3LocalVal(fieldVal.address3)
      } 

      if (cityLocalVal !== fieldVal.city) {
        setCityLocalVal(fieldVal.city)
      } 

      if (countyLocalVal !== fieldVal.county) {
        setCountyLocalVal(fieldVal.county)
      } 

      if (stateLocalVal !== fieldVal.state) {
        setStateLocalVal(fieldVal.state)
      } 

      if (postalCodeLocalVal !== fieldVal.postalCode) {
        setPostalCodeLocalVal(fieldVal.postalCode)
      } 

      if (countryLocalVal !== fieldVal.country) {
        setCountryLocalVal(fieldVal.country)
      } 

    }

  }, [fieldVal])


    var address1Def = formulateSubDefinitions("address1")
    var address2Def = formulateSubDefinitions("address2")
    var address3Def = formulateSubDefinitions("address3")
    var cityDef = formulateSubDefinitions("city")
    var countyDef = formulateSubDefinitions("county")
    var stateDef = formulateSubDefinitions("state")
    var postalCodeDef = formulateSubDefinitions("postalCode")
    var countryDef = formulateSubDefinitions("country")


    var stateFieldType = "Text"
    if (stateDef && stateDef.autoSuggestURL && stateDef.autoSuggestURL != "") {
      stateFieldType = "Autocomplete"
    }

    var suppressTopLabel = false
    if (!fieldDefinition.title || fieldDefinition.title == "" || ((fieldDefinition.title && fieldDefinition.title.trim() == ""))) {
      suppressTopLabel = true
    }

  if (fieldDefinition.mode == "Display") {
    return (
      <ICGFormControl fieldDefinition={fieldDefinition} policyMessages={null}> 
      {/* <Grid container spacing={1}> */}
        {fieldVal && <div className={classes.displayText}>
          <address className={classes.container}>
            <div name={(address1Def||{key:"address1"}).key}>{address1LocalVal}</div>
            <div name={(address2Def||{key:"address2"}).key}>{address2LocalVal}</div>
            <div name={(address3Def||{key:"address3"}).key}>{address3LocalVal || ""}</div>
            <div name={(countyDef||{key:"county"}).key}>{countyLocalVal || ""}</div>
            <div name={(cityDef||{key:"city"}).key}>
              {cityLocalVal}, {stateLocalVal}{" "}
              {postalCodeLocalVal}, {countryLocalVal || ""}
            </div>
          </address>
        </div>}
        {!fieldVal && "--"}
       {/* </Grid> */}
      </ICGFormControl> 
    )

  } else {
    return (
      <ICGFormControl suppressLabel={suppressTopLabel} fieldDefinition={fieldDefinition} policyMessages={headerPolicyMessages}> 
      <Grid container spacing={1}>
        
        {/* Address1 */}
        {address1Def && address1Def.mode != "Hidden" &&
        <Grid item xl={address1Def?.gridAttributes.xl || 12} lg={address1Def?.gridAttributes.lg || 12} md={address1Def?.gridAttributes.md || 12} sm={address1Def?.gridAttributes.sm || 12} xs={address1Def?.gridAttributes.xs || 12}>
          <Address1Context.Provider value={[address1LocalVal, setAddress1LocalVal]}>
            <TextField
              fieldDefinition={address1Def}
              policyMessages={address1PolicyMessages}
              readOnlyOverride={readOnlyOverride}
              path={path} 
              onValidate={onValidate}
              onAccessRootData={onAccessRootData}
              contextType={"address1"}
            />
          </Address1Context.Provider>
        </Grid>
        }
  
        {/* Address2 */}
        {address2Def && address2Def.mode !== "Hidden" &&
        <Grid item xl={address2Def?.gridAttributes.xl || 12} lg={address2Def?.gridAttributes.lg || 12} md={address2Def?.gridAttributes.md || 12} sm={address2Def?.gridAttributes.sm || 12} xs={address2Def?.gridAttributes.xs || 12}>
          <Address2Context.Provider value={[address2LocalVal, setAddress2LocalVal]}>
            <TextField
            fieldDefinition={address2Def}
            policyMessages={address2PolicyMessages}
            readOnlyOverride={readOnlyOverride}
            contextType={"address2"}
            />
          </Address2Context.Provider>
        </Grid>
        }
  
        {/* Address3 */}
        {address3Def && address3Def.mode !== "Hidden" &&
        <Grid item xl={address3Def?.gridAttributes.xl || 12} lg={address3Def?.gridAttributes.lg || 12} md={address3Def?.gridAttributes.md || 12} sm={address3Def?.gridAttributes.sm || 12} xs={address3Def?.gridAttributes.xs || 12}>
          <Address3Context.Provider value={[address3LocalVal, setAddress3LocalVal]}>
            <TextField
              fieldDefinition={address3Def}
              policyMessages={address3PolicyMessages}
              readOnlyOverride={readOnlyOverride}
              contextType={"address3"}
            />
          </Address3Context.Provider>
        </Grid>
        }
  
        {/* County */}
        {countyDef && countyDef.mode !== "Hidden" &&
        <Grid item xl={countyDef?.gridAttributes.xl || 12} lg={countyDef?.gridAttributes.lg || 12} md={countyDef?.gridAttributes.md || 12} sm={countyDef?.gridAttributes.sm || 12} xs={countyDef?.gridAttributes.xs || 12}>
          <CountyContext.Provider value={[countyLocalVal, setCountyLocalVal]}>
            <TextField
              fieldDefinition={countyDef}
              policyMessages={countyPolicyMessages}
              readOnlyOverride={readOnlyOverride}
              contextType={"county"}
            />
          </CountyContext.Provider>
        </Grid>
        }
  
        {/* City */}
        {cityDef && cityDef.mode !== "Hidden" &&
        <Grid item xl={cityDef?.gridAttributes.xl || 6} lg={cityDef?.gridAttributes.lg || 6} md={cityDef?.gridAttributes.md || 6} sm={cityDef?.gridAttributes.sm || 6} xs={cityDef?.gridAttributes.xs || 6}>
          <CityContext.Provider value={[cityLocalVal, setCityLocalVal]}>
            <TextField
            fieldDefinition={cityDef}
            policyMessages={cityPolicyMessages}
            readOnlyOverride={readOnlyOverride}
            contextType={"city"}
            />
          </CityContext.Provider>
        </Grid>
        }
  
  
        {/* State */}
        {stateDef && stateDef.mode !== "Hidden" &&
        <Grid item xl={stateDef?.gridAttributes.xl || 2} lg={stateDef?.gridAttributes.lg || 2} md={stateDef?.gridAttributes.md || 2} sm={stateDef?.gridAttributes.sm || 2} xs={stateDef?.gridAttributes.xs || 2}>
          <StateContext.Provider value={[stateLocalVal, setStateLocalVal]}>
            {stateFieldType == "Text" && 
            <TextField
            fieldDefinition={stateDef}
            policyMessages={statePolicyMessages}
            readOnlyOverride={readOnlyOverride}
            contextType={"state"}
            />}
            {stateFieldType == "Autocomplete" && 
            <AutocompleteField
            fieldDefinition={stateDef}
            policyMessages={statePolicyMessages}
            readOnlyOverride={readOnlyOverride}
            contextType={"state"}
            />}
          </StateContext.Provider>
        </Grid>
        }
  
        {/* Postal Code */}
        {postalCodeDef && postalCodeDef.mode !== "Hidden" &&
        <Grid item xl={postalCodeDef?.gridAttributes.xl || 4} lg={postalCodeDef?.gridAttributes.lg || 4} md={postalCodeDef?.gridAttributes.md || 4} sm={postalCodeDef?.gridAttributes.sm || 4} xs={postalCodeDef?.gridAttributes.xs || 4}>
          <PostalCodeContext.Provider value={[postalCodeLocalVal, setPostalCodeLocalVal]}>
            <TextField
              fieldDefinition={postalCodeDef}
              policyMessages={postalCodePolicyMessages}
              readOnlyOverride={readOnlyOverride}
              contextType={"postalCode"}
            />
          </PostalCodeContext.Provider>
        </Grid>
        }
  
        {/* Country */}
        {countryDef && countryDef.mode !== "Hidden" &&
        <Grid item xl={countryDef?.gridAttributes.xl || 12} lg={countryDef?.gridAttributes.lg || 12} md={countryDef?.gridAttributes.md || 12} sm={countryDef?.gridAttributes.sm || 12} xs={countryDef?.gridAttributes.xs || 12}>
          <CountryContext.Provider value={[countryLocalVal, setCountryLocalVal]}>
            <TextField
            fieldDefinition={countryDef}
            policyMessages={countryPolicyMessages}
            readOnlyOverride={readOnlyOverride}
            contextType={"country"}
            />
          </CountryContext.Provider>
        </Grid>
        }
        
      </Grid>
      </ICGFormControl> 
    )
  }
  

};

export default AddressField;
